<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useFinanceApi } from '../composables/useFinanceApi'
import { firstDayOfMonth, formatAmount, formatCount, formatMoney, toIsoDate } from '../utils/format'
import type { Option, TimesheetReportRow } from '../types/finance'

const { t } = useI18n()
const { timesheetReport, listWorkplaceOptions } = useFinanceApi()

const PER_PAGE = 25

const rows = ref<TimesheetReportRow[]>([])
const departments = ref<Option[]>([])
const loading = ref(true)
const error = ref('')
const page = ref(1)

const filters = ref({
  date_from: firstDayOfMonth(),
  date_to: toIsoDate(),
  department_agent_id: '' as string | number,
  search: '',
})

const validationError = computed(() =>
  filters.value.date_from > filters.value.date_to ? t('finance.common.invalidRange') : '',
)

const visibleRows = computed(() => {
  const term = filters.value.search.trim().toLowerCase()
  if (!term) return rows.value
  return rows.value.filter((row) =>
    [row.full_name, row.employee_code].some((value) => String(value).toLowerCase().includes(term)),
  )
})

const sortedRows = computed(() =>
  [...visibleRows.value].sort((a, b) => Number(b.total_bill_amount) - Number(a.total_bill_amount)),
)

const totalPages = computed(() => Math.max(1, Math.ceil(sortedRows.value.length / PER_PAGE)))
const pagedRows = computed(() =>
  sortedRows.value.slice((page.value - 1) * PER_PAGE, page.value * PER_PAGE),
)

const rangeLabel = computed(() => {
  const total = sortedRows.value.length
  if (!total) return ''
  const from = (page.value - 1) * PER_PAGE + 1
  const to = Math.min(page.value * PER_PAGE, total)
  return t('finance.common.showing', { from, to, total })
})

/** Labour cost per currency — rows carry the currency they were billed in. */
const totalsByCurrency = computed(() => {
  const totals = new Map<string, number>()
  for (const row of visibleRows.value) {
    const code = row.currency_code || '—'
    totals.set(code, (totals.get(code) ?? 0) + Number(row.total_bill_amount ?? 0))
  }
  return [...totals.entries()]
    .map(([code, amount]) => ({ code, amount }))
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
})

const totalHours = computed(() =>
  visibleRows.value.reduce((sum, row) => sum + Number(row.total_hours ?? 0), 0),
)

const totalCycles = computed(() =>
  visibleRows.value.reduce((sum, row) => sum + Number(row.total_cycles ?? 0), 0),
)

const totalEntries = computed(() =>
  visibleRows.value.reduce((sum, row) => sum + Number(row.total_entries ?? 0), 0),
)

async function load() {
  loading.value = true
  error.value = ''
  if (validationError.value) {
    error.value = validationError.value
    loading.value = false
    return
  }
  try {
    rows.value = await timesheetReport({
      date_from: filters.value.date_from,
      date_to: filters.value.date_to,
      department_agent_id: filters.value.department_agent_id,
    })
    page.value = 1
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadDepartments() {
  try {
    departments.value = await listWorkplaceOptions()
  } catch {
    departments.value = []
  }
}

watch(
  () => [filters.value.date_from, filters.value.date_to, filters.value.department_agent_id],
  () => {
    void load()
  },
)

watch(
  () => filters.value.search,
  () => {
    page.value = 1
  },
)

onMounted(() => {
  void loadDepartments()
  void load()
})
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('finance.payroll.title') }}</h2>
  </div>

  <div class="toolbar">
    <input v-model="filters.date_from" class="date-input" type="date">
    <input v-model="filters.date_to" class="date-input" type="date">
    <select v-model="filters.department_agent_id" class="filter-select">
      <option value="">{{ t('finance.common.all') }}</option>
      <option v-for="department in departments" :key="department.id" :value="department.id">
        {{ department.name }}
      </option>
    </select>
    <input
      v-model="filters.search"
      class="search-input"
      type="search"
      :placeholder="t('finance.common.searchPlaceholder')"
    >
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('finance.common.apply') }}
    </button>
  </div>

  <section class="notice">
    <strong>{{ t('finance.payroll.gapTitle') }}</strong>
    <p>{{ t('finance.payroll.gapNotice') }}</p>
  </section>

  <div v-if="loading && rows.length === 0" class="state-message">
    <p>{{ t('finance.common.loading') }}</p>
  </div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <div v-else-if="visibleRows.length === 0" class="state-message">
    <p>{{ t('finance.payroll.empty') }}</p>
  </div>

  <template v-else>
    <div class="summary-row">
      <div class="summary-card accent">
        <span class="summary-label">{{ t('finance.payroll.labourCost') }}</span>
        <span v-for="line in totalsByCurrency" :key="line.code" class="summary-value">
          {{ formatMoney(line.amount, line.code) }}
        </span>
      </div>
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.payroll.hours') }}</span>
        <span class="summary-value">{{ formatCount(totalHours) }}</span>
        <span class="summary-label">
          {{ t('finance.payroll.cycles') }}: {{ formatCount(totalCycles) }}
        </span>
      </div>
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.payroll.employees') }}</span>
        <span class="summary-value">{{ visibleRows.length }}</span>
        <span class="summary-label">
          {{ t('finance.payroll.entries') }}: {{ formatCount(totalEntries) }}
        </span>
      </div>
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.payroll.period') }}</span>
        <span class="summary-value">{{ filters.date_from }} — {{ filters.date_to }}</span>
      </div>
    </div>

    <div class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.payroll.employee') }}</th>
            <th class="cell-num">{{ t('finance.payroll.entries') }}</th>
            <th class="cell-num">{{ t('finance.payroll.hours') }}</th>
            <th class="cell-num">{{ t('finance.payroll.cycles') }}</th>
            <th class="cell-num">{{ t('finance.payroll.labourCost') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in pagedRows" :key="row.employee_id">
            <td class="cell-name">
              {{ row.full_name }}
              <span class="cell-sub">{{ row.employee_code }}</span>
            </td>
            <td class="cell-num">{{ formatCount(row.total_entries) }}</td>
            <td class="cell-num">{{ formatCount(row.total_hours) }}</td>
            <td class="cell-num">{{ formatCount(row.total_cycles) }}</td>
            <td class="cell-num">
              {{ formatMoney(row.total_bill_amount, row.currency_code) }}
            </td>
          </tr>
          <tr class="row-total">
            <td>{{ t('finance.payroll.total') }}</td>
            <td class="cell-num">{{ formatCount(totalEntries) }}</td>
            <td class="cell-num">{{ formatCount(totalHours) }}</td>
            <td class="cell-num">{{ formatCount(totalCycles) }}</td>
            <td class="cell-num">
              <template v-if="totalsByCurrency.length">
                <span v-for="line in totalsByCurrency" :key="line.code">
                  {{ formatAmount(line.amount) }} {{ line.code }}
                </span>
              </template>
              <template v-else>—</template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="totalPages > 1" class="pagination">
      <span>{{ rangeLabel }}</span>
      <button :disabled="page <= 1" @click="page -= 1">{{ t('finance.common.previous') }}</button>
      <span>{{ t('finance.common.page', { page, total: totalPages }) }}</span>
      <button :disabled="page >= totalPages" @click="page += 1">
        {{ t('finance.common.next') }}
      </button>
    </div>
  </template>
</template>
